import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios, { isAxiosError } from 'axios';
import { useNavigate, useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { QrCodeIcon, UserXIcon } from 'lucide-react';
import { validate as validateUUID } from 'uuid';

import config from 'constants/apiConfig';
import {
  updateCurrentIncident,
  updateCurrentlySelectedGroup,
} from 'actions/diceActions';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { incidentQueryOptions } from 'components/IAP/hooks/incidentHooks';
import { Button } from 'components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from 'components/ui/empty';
import type { WorkLog } from 'hooks/incident/useWorkLog';
import {
  NOT_ON_ROSTER_MESSAGE,
  parseQrAction,
  qrProfilePath,
} from './incidentQr';

type Group = { group_guid: string };

const isForbiddenOrMissing = (error: unknown) => {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status === 403 || status === 404;
};

/**
 * Where a printed incident QR code lands: checks the scanner is on the roster,
 * switches to the incident's organization, then opens their incident profile
 * with the scanned control ready.
 */
export function QrIncidentRedirect() {
  const { incidentId = '', action: rawAction } = useParams<{
    incidentId: string;
    action: string;
  }>();
  const action = parseQrAction(rawAction);
  const validLink = !!action && validateUUID(incidentId);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userGuid = useAppSelector(selectUserGuid);
  const currentGroupGuid = useAppSelector(selectGroupGuid);
  const groups = useAppSelector((state) => state.app.groups as Group[]);
  const areGroupsLoaded = useAppSelector(
    (state) => state.app.areGroupsLoaded as boolean
  );
  const redirected = useRef(false);

  const incident = useQuery({
    ...incidentQueryOptions(incidentId),
    enabled: validLink,
    retry: false,
  });

  const workLog = useQuery({
    queryKey: ['qr-roster-check', incidentId, userGuid],
    queryFn: async () => {
      const resp = await axios.get<WorkLog>(
        `${config.apiGateway.incident}/${incidentId}/users/${userGuid}/work-logs`
      );
      return resp.data;
    },
    enabled: validLink && !!userGuid,
    retry: false,
  });

  const incidentGroupGuid = (
    incident.data as { group_guid?: string } | undefined
  )?.group_guid;
  const targetGroup = incidentGroupGuid
    ? groups?.find((group) => group.group_guid === incidentGroupGuid)
    : undefined;
  const notOnRoster =
    (workLog.isError && isForbiddenOrMissing(workLog.error)) ||
    (incident.isError && isForbiddenOrMissing(incident.error)) ||
    (workLog.isSuccess && !workLog.data?.incidentMemberId) ||
    (!!incidentGroupGuid && areGroupsLoaded && !targetGroup);

  useEffect(() => {
    if (!action || redirected.current || notOnRoster) return;
    if (!incident.data || !workLog.data?.incidentMemberId) return;
    if (incidentGroupGuid && incidentGroupGuid !== currentGroupGuid) {
      if (!targetGroup) return;
      dispatch(updateCurrentlySelectedGroup(targetGroup) as never);
    }
    dispatch(updateCurrentIncident(incident.data) as never);
    redirected.current = true;
    navigate(qrProfilePath(incidentId, action), { replace: true });
  }, [
    action,
    notOnRoster,
    incident.data,
    workLog.data,
    incidentGroupGuid,
    currentGroupGuid,
    targetGroup,
    incidentId,
    dispatch,
    navigate,
  ]);

  const failure = !validLink
    ? {
        icon: <QrCodeIcon />,
        title: 'This QR code is not valid',
        description: 'Ask the planning section for a current poster.',
      }
    : notOnRoster
      ? {
          icon: <UserXIcon />,
          title: 'Not on the roster',
          description: NOT_ON_ROSTER_MESSAGE,
        }
      : incident.isError || workLog.isError
        ? {
            icon: <QrCodeIcon />,
            title: 'This QR code could not be opened',
            description: 'Try scanning again in a moment.',
          }
        : null;

  if (failure) {
    return (
      <Empty className="tw:min-h-[60vh]">
        <EmptyHeader>
          <EmptyMedia variant="icon">{failure.icon}</EmptyMedia>
          <EmptyTitle>{failure.title}</EmptyTitle>
          <EmptyDescription>{failure.description}</EmptyDescription>
        </EmptyHeader>
        <Button
          variant="outline"
          onClick={() => navigate('/hub', { replace: true })}
        >
          Go to dashboard
        </Button>
      </Empty>
    );
  }

  return (
    <Empty className="tw:min-h-[60vh]">
      <EmptyHeader>
        <EmptyTitle>Opening your incident profile…</EmptyTitle>
      </EmptyHeader>
    </Empty>
  );
}

export default QrIncidentRedirect;
